// SKILL-OVERRIDES: a line diff for the skill dialog ("What changed in the default"). A plain
// longest-common-subsequence over lines; past the size cap it falls back to "all removed, all
// added" (a skill's text is a page or two, so the cap is never reached in practice).
export type DiffLine = { kind: "same" | "add" | "del"; text: string };

const CAP = 1_500;

const linesOf = (s: string | null | undefined) => (s ? s.replace(/\r\n/g, "\n").split("\n") : []);

export function lineDiff(before: string | null | undefined, after: string | null | undefined) {
  const a = linesOf(before);
  const b = linesOf(after);
  if (a.length > CAP || b.length > CAP)
    return [
      ...a.map((text) => ({ kind: "del" as const, text })),
      ...b.map((text) => ({ kind: "add" as const, text })),
    ];
  const n = a.length;
  const m = b.length;
  // lcs[i][j]: the LCS length of a[i..] and b[j..].
  const lcs: Uint16Array[] = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i] as Uint16Array;
    const next = lcs[i + 1] as Uint16Array;
    for (let j = m - 1; j >= 0; j--)
      row[j] =
        a[i] === b[j]
          ? (next[j + 1] as number) + 1
          : Math.max(next[j] as number, row[j + 1] as number);
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] as string });
      i++;
      j++;
    } else if (
      ((lcs[i + 1] as Uint16Array)[j] as number) >= ((lcs[i] as Uint16Array)[j + 1] as number)
    ) {
      out.push({ kind: "del", text: a[i] as string });
      i++;
    } else {
      out.push({ kind: "add", text: b[j] as string });
      j++;
    }
  }
  while (i < n) out.push({ kind: "del", text: a[i++] as string });
  while (j < m) out.push({ kind: "add", text: b[j++] as string });
  return out;
}

/** How many lines were added and removed. */
export function diffCounts(lines: readonly DiffLine[]) {
  let added = 0;
  let removed = 0;
  for (const l of lines) {
    if (l.kind === "add") added++;
    else if (l.kind === "del") removed++;
  }
  return { added, removed };
}
